import { useRef, useState, type ReactNode } from 'react';
import InkPad, { type InkPadHandle } from './InkPad';
import SignatureImage from './SignatureImage';

/**
 * One signing popup for every document signed on the screen — the loan
 * receipt, the payslip, the statement, a login's own signature. The shape is
 * the advance popup's: what is being signed for, then the pad; Save is
 * disabled until there is ink, Clear wipes it, "Not now" (or `skipLabel`)
 * leaves it unsigned. Once signed, it shows the stored signature — fetched
 * with the session token, never a plain <img src> — with Sign again and the
 * print action.
 */

interface Props {
  title: string;
  /** "Hand over the cash and have him sign on the tablet." */
  lead: string;
  /** What is being signed for: name, amount, dates. */
  summary?: ReactNode;
  /** "Signature of Sunil Perera" */
  padLabel: string;
  /** The stored signature's API path, when there already is one. */
  signedPath?: string | null;
  signedAt?: string | null;
  onSave: (png: Blob) => Promise<void>;
  onClose: () => void;
  /** Shown beside Close once signed, e.g. "Print payslip". */
  printLabel?: string;
  onPrint?: () => void;
  /** The button that leaves without signing. */
  skipLabel?: string;
  onSkip?: () => void;
}

export default function SignDialog({
  title, lead, summary, padLabel, signedPath, signedAt, onSave, onClose,
  printLabel, onPrint, skipLabel = 'Not now', onSkip,
}: Props) {
  const padRef = useRef<InkPadHandle | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const [resigning, setResigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const showSigned = !!signedPath && !resigning;

  async function save() {
    setSaving(true);
    setError('');
    try {
      if (!padRef.current) throw new Error('There is no signature to save.');
      const png = await padRef.current.toPng();
      await onSave(png);
      setResigning(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the signature.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="mbox">
        <div className="mhead">{title}</div>
        <div className="sub" style={{ marginBottom: 12 }}>{lead}</div>

        {summary && (
          <div className="card" style={{ margin: '0 0 10px', background: 'var(--surface-2)' }}>
            {summary}
          </div>
        )}

        {showSigned ? (
          <>
            <div className="sub">
              Signed {signedAt ? `on ${new Date(signedAt).toLocaleString('en-GB')}` : 'on the tablet'}
            </div>
            <div className="sigdone">
              <SignatureImage path={signedPath!} />
            </div>
            <div className="bar" style={{ marginTop: 12 }}>
              <button type="button" className="btn" onClick={onClose}>Close</button>
              <button type="button" className="btn" onClick={() => setResigning(true)}>Sign again</button>
              {onPrint && (
                <button type="button" className="btn pri" onClick={onPrint}>
                  {printLabel ?? 'Print'}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="sub">{padLabel}</div>
            <InkPad ref={padRef} onInk={setHasInk} hasInk={hasInk} />
            {error && <div className="err">{error}</div>}
            <div className="bar" style={{ marginTop: 10 }}>
              <button type="button" className="btn" onClick={() => padRef.current?.clear()} disabled={saving}>
                Clear
              </button>
              <button type="button" className="btn" onClick={onSkip ?? onClose} disabled={saving}>
                {skipLabel}
              </button>
              <button
                type="button"
                className="btn pri"
                disabled={!hasInk || saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
